import { createSlice } from '@reduxjs/toolkit';
import { fetchVoucher, createVoucher, clearStore, fetchTenants } from './voucherActions';

interface VoucherState {
  loading: boolean;
  voucher: any | null;
  error: string | null;
  activationUserDetails: any | null;
  tenantsLoading?: boolean;
  tenantsError?: string | null;
  tenants?: any[];
  // Retained so a `pending` voucher's status card can re-fetch (poll) the
  // voucher's latest state without asking the user to re-enter their PIN.
  lastSaralCode: string | null;
  lastPin: string | null;
}

const initialState: VoucherState = {
  loading: false,
  voucher: null,
  error: null,
  activationUserDetails: null,
  tenantsLoading: false,
  tenantsError: null,
  tenants: [],
  lastSaralCode: null,
  lastPin: null,
};

const voucherSlice = createSlice({
  name: 'voucher',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearTenantsError: (state) => {
      state.tenantsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch voucher cases
      .addCase(fetchVoucher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVoucher.fulfilled, (state, action) => {
        state.loading = false;
        state.voucher = action.payload;
        state.lastSaralCode = action.meta.arg.saral_code;
        state.lastPin = action.meta.arg.pin;
      })
      .addCase(fetchVoucher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Create voucher cases
      .addCase(createVoucher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVoucher.fulfilled, (state, action) => {
        state.loading = false;
        state.activationUserDetails = action.payload;
      })
      .addCase(createVoucher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Fetch tenants cases
      .addCase(fetchTenants.pending, (state) => {
        state.tenantsLoading = true;
        state.tenantsError = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.tenantsLoading = false;
        state.tenants = action.payload || [];
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.tenantsLoading = false;
        state.tenantsError = action.payload || 'Failed to load tenants';
      })
      // Clear store case
      .addCase(clearStore.fulfilled, () => {
        return initialState;
      });
  },
});

export const { clearError, clearTenantsError, setError } = voucherSlice.actions;
export default voucherSlice.reducer; 